<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import './Login.scss';
import storeAuth from '@store/auth';

import { inputsLogin, twoFactorLogin } from '@services/forms/inputsLogin';

import AuthenticationApi from '@repositories/autenticacion.repository';
import TemplatesUIDataProviderRepository from '@repositories/templatesUI.repository';
import { useI18n } from 'vue-i18n';
import { loadLocaleMessages } from '../../../helpers/lang/loadLocale';
import LanguagesRepository from '@repositories/languages.repository';
import i18n from '../../../helpers/lang/i18n';
import CaptchaCloudflare from './CaptchaCloudflare.vue';

const { t } = useI18n();
const { login } = storeAuth();
const router = useRouter();

const autenticacionController = new AuthenticationApi();
const templateUIController = new TemplatesUIDataProviderRepository();
const imageLogin = ref<string>('');
const titleLogin = ref<string>('');
const refForm = ref<any>(null);
const refFormTwoFactor = ref<any>(null);
const errorMessages = ref<Array<string>>([]);
const remainingSeconds = ref<number>(0);
const isBlocked = computed(() => remainingSeconds.value > 0);
let countdownTimer: ReturnType<typeof setInterval> | null = null;
const loading = ref<boolean>(false);
const qrCode = ref<string>('');
const userId = ref('');
const passwordVisible = ref<boolean>(false);
const loginImage = ref<string>('img/avatar.svg');
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<any>(null);

onMounted(() => {
  imageLogin.value = '/img/bg_home.png';
  addEventViewPassword();
});
onMounted(async () => {
  try {
    const fullPath = window.location.origin;
    const data = await templateUIController.getNameTemplate(fullPath);
    titleLogin.value = data && data.toString();

    const lang = await LanguagesRepository.getLanguageByUrlDomain(
      window.location.origin,
    );

    const locale = lang;
    const messages = loadLocaleMessages(locale);
    i18n.global.setLocaleMessage(locale, messages);
    i18n.global.locale.value = locale;
  } catch (_) {}
});

const resetFocus = () => {
  loginImage.value = 'img/avatar.svg';
};

const stopCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

const blockLogin = (seconds: number) => {
  stopCountdown();
  remainingSeconds.value = seconds;
  countdownTimer = setInterval(() => {
    remainingSeconds.value -= 1;
    if (remainingSeconds.value <= 0) stopCountdown();
  }, 1000);
};

const blockedMessage = computed(() => {
  const minutes = Math.floor(remainingSeconds.value / 60);
  const seconds = remainingSeconds.value % 60;
  const tiempo = `${minutes}:${String(seconds).padStart(2, '0')}`;
  return t('autenticacion.login.demasiados_intentos', { tiempo });
});

// Sólo aplica si el 429 llegó sin Retry-After.
const FALLBACK_BLOCK_SECONDS = 60;

const handleErrors = (error: any) => {
  if (error.status === 429) {
    blockLogin(error.retryAfterSeconds ?? FALLBACK_BLOCK_SECONDS);
    return;
  }

  if (error.errors?.length) {
    error.errors.forEach((k: any) => {
      errorMessages.value.push(k.msg);
    });
    return;
  }
  if (error.message) {
    errorMessages.value.push(error.message);
  }
};

// Limpia los mensajes de error al volver a interactuar con el formulario
// (foco en un campo o cambio de valor), para que no queden pegados de un
// intento anterior.
const clearErrors = () => {
  if (errorMessages.value.length) {
    errorMessages.value = [];
  }
};

const handleFocus = (inputKey: any) => {
  clearErrors();
  if (inputKey.input.key === 'user') {
    loginImage.value = 'img/touch_user.svg';
  } else if (
    inputKey.input.key === 'password' ||
    inputKey.input.key === 'codeTwoFactor'
  ) {
    loginImage.value = 'img/touch_password.svg';
  } else {
    resetFocus();
  }
};

const handleTokenCaptcha = (token: string) => {
  tokenCaptcha.value = token;
};

const handleOnExpired = () => {
  tokenCaptcha.value = null;
};

const loginUser = async () => {
  if (isBlocked.value) return;
  resetFocus();
  if (refForm.value) {
    const { isFormValid, items } = await refForm.value.getValues();
    if (isFormValid) {
      if (!tokenCaptcha.value) {
        errorMessages.value = [
          t('autenticacion.login.por_favor_verifica_el_captcha'),
        ];
        return;
      }
      errorMessages.value = [];
      loading.value = true;
      try {
        const result = await autenticacionController.login({
          ...items,
          captchaToken: tokenCaptcha.value,
        });
        handleLoginResult(result);
      } catch (error) {
        loading.value = false;
      } finally {
        captchaRef.value?.reset();
      }
    } else {
      errorMessages.value.push(
        t('autenticacion.login.debes_completar_todos_los_campos'),
      );
    }
  }
};

const handleLoginResult = async (result: any) => {
  loading.value = false;
  const { error, user, token, refreshToken, qrCodeDataUrl } = result;
  if (error) {
    handleErrors(error);
  } else {
    login({ token, user, refreshToken });
    qrCode.value = qrCodeDataUrl;
    userId.value = user.id;
    router.push({ name: 'Operaciones' });
  }
};

const sendCode = async () => {
  if (refFormTwoFactor.value) {
    errorMessages.value = [];
    const { isFormValid, items } = await refFormTwoFactor.value.getValues();
    if (!isFormValid) {
      return;
    }
    const userData = {
      code: items.codeTwoFactor,
      id: userId.value,
    };
    const result = await autenticacionController.login2fa(userData);
    handleLoginResult(result);
  }
};

// El Form se destruye y se vuelve a montar al pasar al paso de 2FA
// (v-if="!qrCode"), así que un listener atado a un nodo puntual quedaría
// huérfano. Delegamos el click
// en `document` (nunca se destruye) y buscamos el input/imagen vigentes
// en el momento del click.
const handleTogglePasswordClick = (e: Event) => {
  const target = e.target as HTMLElement;
  const wrapper = target?.closest?.('.input-password') as HTMLElement | null;
  if (!wrapper || target.tagName !== 'IMG') return;

  const inputHtml = wrapper.querySelector('input');
  const imgHtml = wrapper.querySelector('img') as HTMLImageElement | null;
  if (!inputHtml || !imgHtml) return;

  if (passwordVisible.value) {
    imgHtml.src = '/icons/ojo.svg';
    inputHtml.setAttribute('type', 'password');
  } else {
    imgHtml.src = '/icons/ojo_x.svg';
    inputHtml.setAttribute('type', 'text');
  }
  passwordVisible.value = !passwordVisible.value;
};

const addEventViewPassword = () => {
  document.addEventListener('click', handleTogglePasswordClick);
};

onBeforeUnmount(() => {
  document.removeEventListener('click', handleTogglePasswordClick);
  stopCountdown();
});
</script>

<template>
  <div class="login-container">
    <div class="login-container__card">
      <div class="login-container__header">
        <div class="login-container__logo-wrap">
          <img
            src="/img/tryger-logo.png"
            alt="Tryger"
            class="login-container__logo"
          />
        </div>
      </div>
      <div class="login-container__content">
        <div
          class="d-flex flex-column justify-content-center align-items-center login-container__form"
        >
          <div v-if="isBlocked" class="alert alert-danger">
            {{ blockedMessage }}
          </div>
          <template v-if="errorMessages.length > 0">
            <div
              v-for="(item, index) in errorMessages"
              :key="index"
              class="alert alert-danger"
            >
              {{ item }}
            </div>
          </template>
          <Overlay
            v-if="loading"
            :show="loading"
            :circleColor="'#40a4c6'"
            :borderColor="'#40a4c6'"
          />
          <template v-if="!qrCode">
            <div class="d-flex justify-content-center">
              <Form
                :inputs="inputsLogin()"
                @focus="handleFocus"
                @change="clearErrors"
                ref="refForm"
              />
            </div>
            <div class="login-container__captcha">
              <CaptchaCloudflare
                ref="captchaRef"
                @verified="handleTokenCaptcha"
                @expired="handleOnExpired"
              />
            </div>
            <CButton
              class="py-2 w-100 my-3"
              :text="t('autenticacion.login.ingresar')"
              :background="`background-blue`"
              :disabled="loading || isBlocked"
              @click="loginUser"
              type="button"
            />
            <div
              class="d-flex justify-content-center contanier-recovery-password mt-3 mb-4"
            >
              <router-link to="/send-mail-recovery">
                <p class="m-0">
                  {{ t('autenticacion.login.olvide_mi_contrasenia') }}
                </p>
              </router-link>
            </div>
          </template>

          <template v-else>
            <b-img
              v-if="qrCode && qrCode !== 'ok'"
              :src="`${qrCode}`"
              rounded
              alt="Rounded image"
              class="d-block m-auto w-25"
            />
            <Form
              :inputs="twoFactorLogin()"
              @focus="handleFocus"
              ref="refFormTwoFactor"
            />
            <div class="w-25 my-2">
              <CButton
                class="py-2 w-100"
                :text="t('autenticacion.login.enviar_codigo')"
                :background="`background-blue`"
                :disabled="loading"
                @click="sendCode"
                type="button"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
